import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  List,
  ListItem,
  Paper,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { mobileChequeService } from '../../../services/mobile/mobileChequeService';
import { exportToExcel } from '../../../utils/excelExport';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import { formatCurrencyAmount, getCurrencyDecimals } from '../../../utils/currencyUtils';
import { format } from 'date-fns';

const STATUS_COLORS = {
  Pending: { bg: '#fef3c7', color: '#d97706' },
  Printed: { bg: '#ede9fe', color: '#7c3aed' },
  Signed: { bg: '#ede7f6', color: '#5e35b1' },
  Cleared: { bg: '#d1fae5', color: '#059669' },
  Cancelled: { bg: '#fee2e2', color: '#dc2626' },
  Bounced: { bg: '#fce7f3', color: '#be185d' },
  Collected: { bg: '#dbeafe', color: '#2563eb' },
};

const MobileReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [allCheques, setAllCheques] = useState([]);
  const [banks, setBanks] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filters
  const [bankId, setBankId] = useState('all');
  const [status, setStatus] = useState('all');
  const [user, setUser] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [signedStatusEnabled, setSignedStatusEnabled] = useState(false);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [chequesRes, banksRes, prefs] = await Promise.all([
          mobileChequeService.getCheques({ PageSize: 10000, PageNumber: 1 }),
          mobileChequeService.getBanks(),
          mobileChequeService.getCompanyPreferences(),
        ]);

        if (chequesRes.success) {
          const data = Array.isArray(chequesRes.data) ? chequesRes.data : [];
          setAllCheques(data);
        }
        if (banksRes.success) {
          setBanks(Array.isArray(banksRes.data) ? banksRes.data : []);
        }
        if (prefs) {
          setSignedStatusEnabled(Boolean(prefs.enableSignedStatus));
        }
      } catch {
        // handled by service
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Distinct users
  const distinctUsers = useMemo(() => {
    const users = allCheques.map(c => c.CreatedBy || c.createdBy).filter(Boolean);
    return [...new Set(users)].sort();
  }, [allCheques]);

  // Filtered data
  const filteredCheques = useMemo(() => {
    // Pre-compute date-only boundaries once per render
    const fromOnly = dateFrom && !isNaN(new Date(dateFrom).getTime())
      ? new Date(new Date(dateFrom).getFullYear(), new Date(dateFrom).getMonth(), new Date(dateFrom).getDate())
      : null;
    const toOnly = dateTo && !isNaN(new Date(dateTo).getTime())
      ? new Date(new Date(dateTo).getFullYear(), new Date(dateTo).getMonth(), new Date(dateTo).getDate())
      : null;

    return allCheques.filter(c => {
      if (bankId !== 'all') {
        const chequeBankId = c.BankId || c.bankId;
        if (String(chequeBankId) !== String(bankId)) return false;
      }
      if (status !== 'all') {
        const chequeStatus = c.Status || c.status;
        if (chequeStatus !== status) return false;
      }
      if (user !== 'all') {
        const chequeUser = c.CreatedBy || c.createdBy;
        if (chequeUser !== user) return false;
      }
      if (fromOnly || toOnly) {
        const raw = c.ChqDate || c.chqDate;
        if (!raw) return false;
        const d = new Date(raw);
        if (isNaN(d.getTime())) return false;
        const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        if (fromOnly && dOnly < fromOnly) return false;
        if (toOnly && dOnly > toOnly) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const beneficiary = (c.BeneficiaryName || c.beneficiaryName || '').toLowerCase();
        const chequeNumber = (c.ChequeNumber || c.chequeNumber || '').toLowerCase();
        const bankName = (c.BankName || c.bankName || '').toLowerCase();
        const remarks = (c.Remarks || c.remarks || '').toLowerCase();
        if (!beneficiary.includes(q) && !chequeNumber.includes(q) && !bankName.includes(q) && !remarks.includes(q)) return false;
      }
      return true;
    });
  }, [allCheques, bankId, status, user, searchQuery, dateFrom, dateTo]);

  // Status counts
  const statusCounts = useMemo(() => ({
    cleared: filteredCheques.filter(c => (c.Status || c.status) === 'Cleared').length,
    uncleared: filteredCheques.filter(c => {
      const s = c.Status || c.status;
      return s === 'Pending' || s === 'Printed';
    }).length,
    cancelled: filteredCheques.filter(c => (c.Status || c.status) === 'Cancelled').length,
    bounced: filteredCheques.filter(c => (c.Status || c.status) === 'Bounced').length,
  }), [filteredCheques]);

  // Total amount
  const totalAmount = useMemo(() => {
    return filteredCheques.reduce((sum, c) => sum + Number(c.ChqAmount || c.chqAmount || 0), 0);
  }, [filteredCheques]);

  const formatAmount = (amount, currency = 'AED') =>
    formatCurrencyAmount(amount, currency, { withSymbol: false });

  // Currency for the aggregate total: selected bank's currency, or AED when "all".
  const totalCurrency = bankId === 'all'
    ? 'AED'
    : (banks.find((b) => String(b.Id || b.id) === String(bankId))?.Currency
        || banks.find((b) => String(b.Id || b.id) === String(bankId))?.currency
        || 'AED');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
    } catch { return ''; }
  };

  const activeFilterCount =
    [bankId, status, user].filter(v => v !== 'all').length +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0);

  // Excel export
  const handleExport = useCallback(() => {
    if (!filteredCheques.length) return;

    const parameters = [];
    if (bankId !== 'all') {
      const bank = banks.find(b => String(b.Id || b.id) === String(bankId));
      if (bank) parameters.push({ label: 'Bank', value: bank.AccountName || bank.accountName });
    }
    if (status !== 'all') parameters.push({ label: 'Status', value: status });
    if (user !== 'all') parameters.push({ label: 'User', value: user });
    if (dateFrom) parameters.push({ label: 'From Date', value: format(new Date(dateFrom), 'dd-MM-yyyy') });
    if (dateTo) parameters.push({ label: 'To Date', value: format(new Date(dateTo), 'dd-MM-yyyy') });

    // Amount decimals: single currency in the result, else 2 (mixed).
    const exportCurrencies = new Set(filteredCheques.map((c) => c.Currency || c.currency || 'AED'));
    const exportDecimals = exportCurrencies.size === 1 ? getCurrencyDecimals([...exportCurrencies][0]) : 2;

    const columns = [
      { header: 'Bank Name', key: 'BankName', type: 'text' },
      { header: 'Chq No', key: 'ChequeNumber', type: 'text' },
      { header: 'Chq Date', key: 'ChqDate', type: 'date' },
      { header: 'Beneficiary', key: 'BeneficiaryName', type: 'text', width: 30 },
      { header: 'Status', key: 'Status', type: 'text' },
      { header: 'Printed By', key: 'CreatedBy', type: 'text' },
      { header: 'Amount', key: 'chqAmount', type: 'amount', decimals: exportDecimals },
    ];

    const exportData = filteredCheques.map(c => ({
      BankName: c.BankName || c.bankName || '',
      ChequeNumber: c.ChequeNumber || c.chequeNumber || '',
      ChqDate: formatDateForDisplay(c.ChqDate || c.chqDate) || '',
      BeneficiaryName: c.BeneficiaryName || c.beneficiaryName || '',
      Status: c.Status || c.status || '',
      CreatedBy: c.CreatedBy || c.createdBy || '',
      chqAmount: Number(c.ChqAmount || c.chqAmount || 0),
    }));

    const total = filteredCheques.reduce((sum, c) => sum + Number(c.ChqAmount || c.chqAmount || 0), 0);

    exportToExcel({
      title: 'Cheque Report',
      fileName: `EasyCheck_Report_${format(new Date(), 'dd-MM-yyyy')}.xlsx`,
      sheetName: 'Summary',
      columns,
      data: exportData,
      parameters,
      totals: { 'Amount': total },
    });
  }, [filteredCheques, bankId, status, user, banks, dateFrom, dateTo]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#2563eb' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
          Reports
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={handleExport}
            disabled={!filteredCheques.length}
            sx={{ backgroundColor: '#f0fdf4', color: '#059669', '&:hover': { backgroundColor: '#d1fae5' } }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setFiltersOpen(!filtersOpen)}
            sx={{
              backgroundColor: activeFilterCount > 0 ? '#eff6ff' : '#f8fafc',
              color: activeFilterCount > 0 ? '#2563eb' : '#64748b',
              '&:hover': { backgroundColor: '#e2e8f0' },
            }}
          >
            <FilterIcon fontSize="small" />
            {activeFilterCount > 0 && (
              <Box sx={{
                position: 'absolute', top: -2, right: -2,
                width: 16, height: 16, borderRadius: '50%',
                backgroundColor: '#2563eb', color: '#fff',
                fontSize: '0.6rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {activeFilterCount}
              </Box>
            )}
          </IconButton>
        </Box>
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search by beneficiary, cheque #, bank..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94a3b8' }} />
            </InputAdornment>
          ),
          sx: { borderRadius: 2, height: 44 }
        }}
        size="small"
        sx={{ mb: 2 }}
      />

      {/* Filters */}
      <Collapse in={filtersOpen}>
        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>Bank Account</InputLabel>
                <Select value={bankId} label="Bank Account" onChange={(e) => setBankId(e.target.value)}>
                  <MenuItem value="all">All Banks</MenuItem>
                  {banks.map((bank) => {
                    const id = bank.Id || bank.id;
                    const name = bank.AccountName || bank.accountName || '';
                    const currency = bank.Currency || bank.currency || '';
                    return (
                      <MenuItem key={id} value={id}>{name} ({currency})</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                  <MenuItem value="all">All Statuses</MenuItem>
                  {['Pending', 'Printed', ...(signedStatusEnabled ? ['Signed'] : []), 'Cleared', 'Cancelled', 'Bounced', 'Collected'].map((s) => (
                    <MenuItem key={s} value={s}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_COLORS[s]?.color }} />
                        {s}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>User</InputLabel>
                <Select value={user} label="User" onChange={(e) => setUser(e.target.value)}>
                  <MenuItem value="all">All Users</MenuItem>
                  {distinctUsers.map((u) => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <DatePicker
                  label="From Date"
                  value={dateFrom}
                  onChange={(v) => setDateFrom(v)}
                  format="dd-MM-yyyy"
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                    field: { clearable: true, onClear: () => setDateFrom(null) },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={dateTo}
                  onChange={(v) => setDateTo(v)}
                  format="dd-MM-yyyy"
                  minDate={dateFrom || undefined}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                    field: { clearable: true, onClear: () => setDateTo(null) },
                  }}
                />
              </Box>
              {activeFilterCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: '#2563eb', cursor: 'pointer', textAlign: 'center', fontWeight: 600 }}
                  onClick={() => {
                    setBankId('all'); setStatus('all'); setUser('all');
                    setDateFrom(null); setDateTo(null);
                  }}
                >
                  Clear All Filters
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Collapse>

      {/* Status Summary Chips */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
        {[
          { label: 'Cleared', count: statusCounts.cleared, ...STATUS_COLORS.Cleared },
          { label: 'Uncleared', count: statusCounts.uncleared, ...STATUS_COLORS.Pending },
          { label: 'Cancelled', count: statusCounts.cancelled, ...STATUS_COLORS.Cancelled },
          { label: 'Bounced', count: statusCounts.bounced, ...STATUS_COLORS.Bounced },
        ].map((item) => (
          <Chip
            key={item.label}
            label={`${item.label}: ${item.count}`}
            size="small"
            sx={{
              backgroundColor: item.bg,
              color: item.color,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 26,
            }}
          />
        ))}
      </Box>

      {/* Total */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
          {filteredCheques.length} cheque{filteredCheques.length !== 1 ? 's' : ''}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontFamily: 'monospace' }}>
          {formatAmount(totalAmount, totalCurrency)}
        </Typography>
      </Paper>

      {/* Cheque List */}
      {filteredCheques.length === 0 ? (
        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No cheques match the selected filters</Typography>
        </Card>
      ) : (
        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <List disablePadding>
            {filteredCheques.map((cheque, index) => {
              const id = cheque.Id || cheque.id;
              const beneficiary = cheque.BeneficiaryName || cheque.beneficiaryName || '';
              const amount = cheque.ChqAmount || cheque.chqAmount || 0;
              const chqDate = cheque.ChqDate || cheque.chqDate || '';
              const chqNumber = cheque.ChequeNumber || cheque.chequeNumber || '';
              const chequeStatus = cheque.Status || cheque.status || 'Pending';
              const bankName = cheque.BankName || cheque.bankName || '';
              const colors = STATUS_COLORS[chequeStatus] || STATUS_COLORS.Pending;

              return (
                <Box key={id || index}>
                  <ListItem sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', flex: 1, mr: 1 }} noWrap>
                          {beneficiary}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {formatAmount(amount, cheque.Currency || cheque.currency)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            #{chqNumber}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {formatDate(chqDate)}
                          </Typography>
                        </Box>
                        <Chip
                          label={chequeStatus}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            backgroundColor: colors.bg,
                            color: colors.color,
                          }}
                        />
                      </Box>
                      {bankName && (
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem' }}>
                          {bankName}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                  {index < filteredCheques.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        </Card>
      )}
    </Box>
  );
};

export default MobileReportsPage;
